import React, { useState } from "react";

import {
  AlertCircle,
  Download,
  ExternalLink,
  FolderOpen,
  Link as LinkIcon,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";

import { PlanCard } from "./PlanCard";
import { ComingSoon, shortDate } from "./shared";
import {
  apiErrorMessage,
  useAccountPlanActions,
} from "../../Hooks/useAccountPlan";
import type { PlanLink } from "../../Types/AccountPlan";

interface Props {
  accountId: string;
  links: PlanLink[];
  /** Only the AE adds, removes, and (later) ingests from links. */
  canEdit: boolean;
  onChanged: () => void;
  /** Inside the upload card, under the drop zone: no card of its own (2026-11-02). */
  bare?: boolean;
}

export type LinkKind = "file" | "folder";

/**
 * Mock v4 `detectLinkKind`: a folder link is where new plan versions land;
 * anything else is treated as the plan file itself. Null when not a web link.
 *
 * Real sharing links (checked against each service's share-link formats):
 * - Google: docs.google.com/presentation/… and drive.google.com/file/d/… are
 *   files; …/drive/folders/… and …/drive/u/0/folders/… are folders.
 * - SharePoint share links carry a type code: /:f:/ is a folder, /:p:/ /:b:/
 *   /:w:/ /:x:/ /:u:/ … are files. A library view (…/Shared Documents/Forms/
 *   AllItems.aspx?id=…) is a folder.
 * - OneDrive short links: 1drv.ms/f/… is a folder, 1drv.ms/p/… (and other
 *   letters) a file.
 * - Box: /folder/<id> is a folder, /file/<id> a file. A shared link
 *   (app.box.com/s/<token>) doesn't say which; it's treated as a file, since
 *   AEs mostly share the plan deck itself.
 * - Dropbox: /scl/fo/… (and legacy /sh/…) are folders, /scl/fi/… and /s/… files.
 */
export const detectLinkKind = (url: string): LinkKind | null => {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  if (!/^https?:$/.test(u.protocol)) return null;
  let path = u.pathname;
  try {
    path = decodeURIComponent(u.pathname);
  } catch {
    // Keep the raw path for malformed escapes.
  }
  const host = u.hostname.toLowerCase();
  const last = path.split("/").filter(Boolean).pop() || "";
  if (
    /\.(pptx?|pdf|key|docx?)$/i.test(last) ||
    /docs\.google\.com\/presentation/.test(url)
  )
    return "file";
  // Share links whose type is in the path, not in a file name.
  if (host.endsWith("sharepoint.com") && /^\/:([a-z]):\//i.test(path))
    return /^\/:f:\//i.test(path) ? "folder" : "file";
  if (host === "1drv.ms") return /^\/f\//i.test(path) ? "folder" : "file";
  if (host.endsWith("dropbox.com") && /^\/(scl\/fo|sh)\//i.test(path))
    return "folder";
  if (
    /\/folders?\//i.test(path) ||
    /\/(Shared Documents|Documents|Forms)(\/|$)/i.test(path) ||
    path.endsWith("/") ||
    /dropbox\.com\/home/.test(url)
  )
    return "folder";
  return "file";
};

const PlanLinksCard: React.FC<Props> = ({
  accountId,
  links,
  canEdit,
  onChanged,
  bare = false,
}) => {
  const { addPlanLink, deletePlanLink, markPlanLinkOpened } =
    useAccountPlanActions();
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const addLink = async () => {
    const url = linkUrl.trim();
    if (!url) {
      setLinkError(
        "Paste a link to the plan file or the folder where plan versions are saved."
      );
      return;
    }
    if (!detectLinkKind(url)) {
      setLinkError(
        "That doesn't look like a web link — it should start with https://"
      );
      return;
    }
    setSaving(true);
    setLinkError(null);
    try {
      await addPlanLink(accountId, url);
      setLinkUrl("");
      onChanged();
    } catch (err) {
      // Includes the server's 409 "This link is already tracked for this account."
      setLinkError(apiErrorMessage(err, "Couldn't add that link."));
    } finally {
      setSaving(false);
    }
  };

  const removeLink = async (id: string) => {
    setRemovingId(id);
    setLinkError(null);
    try {
      await deletePlanLink(accountId, id);
      setConfirmingId(null);
      onChanged();
    } catch (err) {
      setLinkError(apiErrorMessage(err, "Couldn't remove that link."));
    } finally {
      setRemovingId(null);
    }
  };

  // A plain function, not a component: a new component type each render would
  // remount the input and drop focus while typing.
  const wrap = (children: React.ReactNode) =>
    bare ? (
      <div>
        <div className="mb-2 text-xs font-medium text-gray-600">
          Or link to where the team keeps the editable deck (SharePoint, Drive)
          — for reference only; ExecBridge reads only uploaded files.
        </div>
        {children}
      </div>
    ) : (
      <PlanCard
        title="Links to the plan file"
        subtitle="Where the team keeps the editable deck (SharePoint, Drive). For reference only: ExecBridge reads only uploaded files."
      >
        {children}
      </PlanCard>
    );

  return wrap(
    <>
      {canEdit && (
        <form
          className="mb-3"
          noValidate
          onSubmit={e => {
            e.preventDefault();
            void addLink();
          }}
        >
          <div className="flex gap-2">
            <div
              className={`flex min-w-0 flex-1 items-center gap-2 rounded-lg border bg-gray-50 px-3 py-2 focus-within:border-purple-400 focus-within:ring-2 focus-within:ring-purple-100 ${
                linkError ? "border-rose-300" : "border-gray-200"
              }`}
            >
              <LinkIcon
                className="h-4 w-4 shrink-0 text-gray-400"
                aria-hidden
              />
              <input
                type="url"
                value={linkUrl}
                onChange={e => {
                  setLinkUrl(e.target.value);
                  if (linkError) setLinkError(null);
                }}
                placeholder="Paste a link to the plan file or its folder — it opens in Google Slides, Drive, SharePoint or Box with your own access"
                aria-label="Plan link URL"
                aria-invalid={!!linkError}
                aria-describedby={linkError ? "plan-link-error" : undefined}
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#974ae4] px-3 py-2 text-sm font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Plus className="h-4 w-4" aria-hidden />
              )}{" "}
              Add
            </button>
          </div>
        </form>
      )}
      {linkError && (
        <p
          id="plan-link-error"
          role="alert"
          className="-mt-1.5 mb-3 flex items-center gap-1 text-xs text-rose-600"
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {linkError}
        </p>
      )}
      {links.length === 0 ? (
        <p className="px-1 text-xs italic text-gray-400">
          {canEdit
            ? "No links tracked — paste one above."
            : "No links tracked."}
        </p>
      ) : (
        <ul className="space-y-2">
          {links.map(l => {
            const kind = detectLinkKind(l.url) || "file";
            const KindIcon = kind === "folder" ? FolderOpen : LinkIcon;
            const confirming = confirmingId === l.id;
            return (
              <li
                key={l.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5"
              >
                <KindIcon
                  className="h-4 w-4 shrink-0 text-purple-500"
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="shrink-0 rounded border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase text-gray-600">
                      {kind === "folder" ? "Folder" : "File"}
                    </span>
                    <span className="truncate font-mono text-xs text-gray-700">
                      {l.url}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-gray-500">
                    Added by {l.added_by_name || "someone"} ·{" "}
                    {shortDate(l.created_at)} ·{" "}
                    {l.last_opened_at
                      ? `last opened ${shortDate(l.last_opened_at)}`
                      : "not opened yet"}
                  </div>
                </div>
                {canEdit && !confirming && (
                  <ComingSoon
                    label="Ingest from link"
                    icon={Download}
                    title="Pulling a new version straight from the link comes later — upload the file below for now."
                  />
                )}
                <a
                  // Re-checked at render: a stored link that isn't http(s) is never clickable.
                  href={detectLinkKind(l.url) ? l.url : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    void markPlanLinkOpened(accountId, l.id)
                      .then(onChanged)
                      .catch(() => undefined)
                  }
                  className="inline-flex items-center gap-1 rounded text-xs font-semibold text-[#8538d4] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                >
                  Open <ExternalLink className="h-3 w-3" aria-hidden />
                </a>
                {canEdit &&
                  (confirming ? (
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className="font-medium text-gray-700">Remove?</span>
                      <button
                        type="button"
                        onClick={() => void removeLink(l.id)}
                        disabled={removingId === l.id}
                        className="rounded bg-red-600 px-2 py-1 font-semibold text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 disabled:opacity-50"
                      >
                        Remove
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingId(null)}
                        className="rounded border border-gray-300 bg-white px-2 py-1 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmingId(l.id)}
                      aria-label={`Remove link ${l.url}`}
                      className="rounded p-1 text-gray-400 hover:bg-white hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  ))}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};

export default PlanLinksCard;
